import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, CardContent, Box, Typography, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Skeleton, Alert as MuiAlert,
} from '@mui/material';
import { People as PeopleIcon } from '@mui/icons-material';
import api from '../../../services/api';

const TIER_COLORS = {
  critical: '#d32f2f',
  high: '#ed6c02',
  moderate: '#ffa726',
  routine: '#4caf50',
};

const STATUS_CONFIG = {
  ai_active: { label: 'AI Active', color: 'success' },
  human_involved: { label: 'Human', color: 'warning' },
  onboarding: { label: 'Onboarding', color: 'info' },
  active: { label: 'Active', color: 'success' },
  paused: { label: 'Paused', color: 'default' },
  completed: { label: 'Complete', color: 'default' },
};

const formatRelativeTime = (ts) => {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
};

const getLatestAssessment = (triage) => {
  if (!triage || triage.status === 'not_assessed') return null;
  const domains = triage.primaryDomains;
  if (!domains || domains.length === 0) return null;
  const top = domains[0];
  return `${top.domain}: ${top.severity}`;
};

export default function ClientQuickView({ onNavigateToClient, onViewAllClients }) {
  const [clients, setClients] = useState(null);
  const [priorities, setPriorities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [clientsRes, prioritiesRes] = await Promise.all([
        api.get('/therapist/clients'),
        api.get('/tass/priorities').catch(() => ({ data: null })),
      ]);
      setClients(clientsRes.data);
      setPriorities(prioritiesRes.data);
    } catch (err) {
      console.error('Error fetching client quickview:', err);
      setError(err.response?.data?.msg || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build a map of clientId -> priority data
  const priorityMap = useMemo(() => {
    if (!priorities?.priorities) return {};
    const map = {};
    for (const tier of ['critical', 'high', 'moderate', 'routine']) {
      for (const p of (priorities.priorities[tier] || [])) {
        map[p.clientId] = p;
      }
    }
    return map;
  }, [priorities]);

  // Sort clients by TASS priority score (highest first)
  const sortedClients = useMemo(() => {
    if (!clients) return [];
    return [...clients].sort((a, b) => {
      const pa = priorityMap[a._id]?.priorityScore ?? 0;
      const pb = priorityMap[b._id]?.priorityScore ?? 0;
      return pb - pa;
    });
  }, [clients, priorityMap]);

  const visibleClients = sortedClients.slice(0, 10);
  const totalCount = sortedClients.length;

  if (loading) {
    return <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />;
  }

  if (error) {
    return (
      <MuiAlert severity="error" action={<Button size="small" onClick={fetchData}>Retry</Button>}>
        {error}
      </MuiAlert>
    );
  }

  if (!visibleClients.length) {
    return null;
  }

  return (
    <Card>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon sx={{ fontSize: 18, color: 'action.active' }} />
            <Typography variant="subtitle1" fontWeight="bold">
              My Clients
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({totalCount})
            </Typography>
          </Box>
          {totalCount > 10 && (
            <Button size="small" onClick={onViewAllClients} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              View all {totalCount} clients →
            </Button>
          )}
        </Box>

        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 1, border: 'none' } }}>
            <TableHead>
              <TableRow sx={{ '& th': { color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Tier</TableCell>
                <TableCell>Last Active</TableCell>
                <TableCell>Top Domain</TableCell>
                <TableCell align="center">Alerts</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleClients.map((client, idx) => {
                const priority = priorityMap[client._id];
                const tier = priority?.tier || 'routine';
                const status = client.latestConversationStatus || 'no_conversation';
                const statusCfg = STATUS_CONFIG[status] || { label: status.replace(/_/g, ' '), color: 'default' };
                const assessment = getLatestAssessment(client.triage);
                const alertCount = client.alerts?.pendingCount || 0;

                return (
                  <TableRow
                    key={client._id}
                    hover
                    onClick={() => onNavigateToClient(client._id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                      height: 36,
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.825rem' }}>
                        {client.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusCfg.label}
                        size="small"
                        color={statusCfg.color}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: TIER_COLORS[tier] || TIER_COLORS.routine,
                          mx: 'auto',
                        }}
                        title={tier}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {formatRelativeTime(client.lastActivity)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {assessment || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {alertCount > 0 ? (
                        <Chip
                          label={alertCount}
                          size="small"
                          color="error"
                          sx={{ height: 20, minWidth: 28, fontSize: '0.7rem', fontWeight: 600 }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {totalCount > 10 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
            <Button size="small" onClick={onViewAllClients} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              View all {totalCount} clients →
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
